import {
  AvadresseService,
  AvBemerkungMasterDetailEntry,
  AvbemerkungService,
  AvflaechenanteilService,
  AvgbplanService,
  GbplanMasterDetailEntry,
  GrundstueckadresseDetailEntry,
  GrundstueckFlaechenanteilMasterDetailEntry,
  GrundstueckId,
  GrundstueckListEntry
} from '../../../core/av-api';
import { Component, Input, OnDestroy } from '@angular/core';
import { AppConfigService } from '../../../core/services/app-config.service';
import { keysToTranslate } from 'i18n/keys-to-translate';
import { FormatConfigService } from '../../../core/services/format-config.service';
import { ComponentLocationStrategy } from '../../../shared/services/location-strategy/component-location-strategy';
import { GrundstueckBeschreibungLocationStrategy } from '../../../shared/services/location-strategy/grundstueck-detail-beschreibung-location-strategy.';
import { ComparatorService } from '../../../shared/services/comparator-service';
import { NavTarget } from '../../../shared/components/link/link.component';
import { AbstractComponent } from '../../../shared/components/abstract-component/abstract.component';
import { TimepointService } from '../../../core/services/timepoint.service';
import { Observable, Subscription } from 'rxjs';
import { ErweiterungConfigResponse } from '../../../core/base-api';
import { ListLoaderObservable } from '../../../shared/components/list-loader/list-loader-observable';
import { ListLoader } from '../../../shared/components/list-loader/list-loader';
import { map } from 'rxjs/operators';
import { BodenbedeckungViewmodelService, ViewModelBodenbedeckungEntry } from '../../../core/services/bodenbedeckung-viewmodel.service';
import { GrundstueckflaecheComponent } from '../../../shared/widgets/grundstueck/table-grundstueckflaeche/grundstueckflaeche.component';
import { BeschreibungTableWrapperComponent } from '../../../shared/components/table/beschreibung-table-wrapper.component';
import { GrundbuchplaeneTableComponent } from '../../../shared/widgets/grunbuchplaene-table/grundbuchplaene-table.component';
import { ListLoaderWrapperComponent } from '../../../shared/components/list-loader-wrapper/list-loader-wrapper.component';
import { NgTemplateOutlet } from '@angular/common';
import { HistoryDirective } from '../../../shared/directives/history.directive';
import { KompaktTimepointComponent } from '../../../shared/kompaktdarstellung/timepoint/kompakt-timepoint.component';
import { FlaechenanteilTableComponent } from '../../../shared/widgets/flaechenanteil-table/flaechenanteil-table.component';
import { BodenbedeckungTableComponent } from '../../../shared/widgets/bodenbedeckung-table/bodenbedeckung-table.component';
import { GebaeudeComponent } from '../../../shared/widgets/grundstueck/table-gebaeude/gebaeude.component';
import { AvbemerkungTableComponent } from '../../../shared/widgets/avbemerkung-table/avbemerkung-table.component';
import { TranslatePipe } from '@ngx-translate/core';
import GrundstueckArtEnum = GrundstueckListEntry.GrundstueckArtEnum;
import LagebezeichnungsTypEnum = GrundstueckadresseDetailEntry.LagebezeichnungsTypEnum;
import ActiveErweiterungenEnum = ErweiterungConfigResponse.ActiveErweiterungenEnum;

@Component({
  selector: 'cap-amtliche-vermessung',
  templateUrl: './amtliche-vermessung.component.html',
  styleUrls: ['./amtliche-vermessung.component.scss'],
  providers: [{ provide: ComponentLocationStrategy, useClass: GrundstueckBeschreibungLocationStrategy }],
  imports: [
    GrundstueckflaecheComponent,
    BeschreibungTableWrapperComponent,
    GrundbuchplaeneTableComponent,
    ListLoaderWrapperComponent,
    NgTemplateOutlet,
    HistoryDirective,
    KompaktTimepointComponent,
    FlaechenanteilTableComponent,
    BodenbedeckungTableComponent,
    GebaeudeComponent,
    AvbemerkungTableComponent,
    TranslatePipe
  ]
})
export class AmtlicheVermessungComponent extends AbstractComponent implements OnDestroy {
  navTargets = NavTarget;

  @Input()
  art: GrundstueckArtEnum;
  @Input()
  hasRight = true;
  @Input()
  tableFullWidth = false;
  @Input()
  flaechenMaessigNichtAusgeschieden: boolean;

  _grundstueckId: GrundstueckId;

  flurnamenListLoader: ListLoader<GrundstueckadresseDetailEntry>;
  plaeneListLoader: ListLoader<GbplanMasterDetailEntry>;
  flaechenanteileListLoader: ListLoader<GrundstueckFlaechenanteilMasterDetailEntry>;
  lagebezeichnungListLoader: ListLoader<GrundstueckadresseDetailEntry>;
  bemerkungListLoader: ListLoader<AvBemerkungMasterDetailEntry>;
  bodenbedeckungListLoader: ListLoader<ViewModelBodenbedeckungEntry>;
  gebaeudeFlaecheAlsBodenbedeckungsArt;
  isErweiterungAvPlusActive: boolean;
  lagebezeichnungFallback: GrundstueckadresseDetailEntry.LagebezeichnungsTypEnum;

  private historischeDatenSubscription: Subscription;

  constructor(
    private planService: AvgbplanService,
    private flaechenanteilServce: AvflaechenanteilService,
    private bodenbedeckunViewModelService: BodenbedeckungViewmodelService,
    private bemerkungService: AvbemerkungService,
    public timepointService: TimepointService,
    private adresseService: AvadresseService,
    private appConfigService: AppConfigService,
    private comparatorService: ComparatorService,
    private appformatConfigService: FormatConfigService
  ) {
    super();
    this.lagebezeichnungFallback = LagebezeichnungsTypEnum.FLURNAME;
    this.addSub(
      this.appformatConfigService.erweiterungsConfig$.subscribe((config) => {
        this.isErweiterungAvPlusActive = config?.activeErweiterungen.includes(ActiveErweiterungenEnum.AV_PLUS);
      })
    );
    this.addSub(
      this.appformatConfigService.parameterConfig$.subscribe((config) => {
        this.gebaeudeFlaecheAlsBodenbedeckungsArt = config?.parameters.GEBAEUDE_FLAECHE_ALS_BODENBEDECKUNGSART;
      })
    );
    keysToTranslate<string>([
      'title.AmtlicheVermessung',
      'grundstueckflaeche',
      'Flaeche',
      'Flaechenanteile',
      'Bodenbedeckung',
      'Teilflaeche',
      'Flurname',
      'FLURNAME',
      'ORTSNAME',
      'STRASSE',
      'Katasternummer'
    ]);
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.historischeDatenSubscription?.unsubscribe();
  }

  @Input()
  set grundstueckId(value: GrundstueckId) {
    this._grundstueckId = value;
    if (!value) {
      return;
    }
    this.historischeDatenSubscription = this.appConfigService.einstellungHistorischeDaten$.subscribe(() => {
      this.update();
    });
  }

  get grundstueckId(): GrundstueckId {
    return this._grundstueckId;
  }

  update() {
    const timepointCtx = this.timepointService.timepointCtx;
    const observableFlurnamen = this.getFlurnamen(timepointCtx);
    const observaleLagebezeichnung = observableFlurnamen.pipe(map((flurnamen) => this.convertLagebezeichnungen(flurnamen)));
    this.plaeneListLoader = new ListLoaderObservable<GbplanMasterDetailEntry>(undefined, 1, this.getGbPlan(timepointCtx));
    this.flurnamenListLoader = new ListLoaderObservable<GrundstueckadresseDetailEntry>(undefined, 1, observableFlurnamen);
    this.lagebezeichnungListLoader = new ListLoaderObservable<GrundstueckadresseDetailEntry>(undefined, 1, observaleLagebezeichnung);
    this.flaechenanteileListLoader = new ListLoaderObservable<GrundstueckFlaechenanteilMasterDetailEntry>(
      undefined,
      1,
      this.getFlaechenanteile(timepointCtx)
    );
    this.bodenbedeckungListLoader = new ListLoaderObservable<ViewModelBodenbedeckungEntry>(
      undefined,
      1,
      this.bodenbedeckunViewModelService.loadBodenbedeckungSichtGrundstueck(this.grundstueckId)
    );
    this.bemerkungListLoader = new ListLoaderObservable<AvBemerkungMasterDetailEntry>(undefined, 1, this.getBemerkungen(timepointCtx));
  }

  private getGbPlan(timepointCtx) {
    return this.planService.loadGbPlanSichtGrundstueck(
      //
      this._grundstueckId.id, //
      timepointCtx.timepointIso, //
      timepointCtx.timepointId, //
      this.appConfigService.einstellungHistorischeDaten //
    );
  }

  private getFlurnamen(timepointCtx) {
    return this.adresseService.loadSichGrundstueck(
      //
      this._grundstueckId.id, //
      timepointCtx.timepointIso, //
      timepointCtx.timepointId, //
      this.appConfigService.einstellungHistorischeDaten //
    );
  }

  private getFlaechenanteile(timepointCtx): Observable<Array<GrundstueckFlaechenanteilMasterDetailEntry>> {
    return this.flaechenanteilServce
      .loadGrundstueckFlaechenanteilSichtGrundstueck(
        //
        this._grundstueckId.id, //
        timepointCtx.timepointIso, //
        timepointCtx.timepointId, //
        this.appConfigService.einstellungHistorischeDaten //
      )
      .pipe(
        map((flaechenanteile) => {
          if (flaechenanteile) {
            return flaechenanteile.sort((a, b) => this.comparatorService.flaechenanteileComparator(a, b, this._grundstueckId));
          }
          return null;
        })
      );
  }

  private getBemerkungen(timepointCtx) {
    return this.bemerkungService.loadAvBemerkungSichtGrundstueck(
      //
      this._grundstueckId.id, //
      timepointCtx.timepointIso, //
      timepointCtx.timepointId, //
      this.appConfigService.einstellungHistorischeDaten //
    );
  }

  private convertLagebezeichnungen(flurnamen: GrundstueckadresseDetailEntry[]): GrundstueckadresseDetailEntry[] {
    if (!flurnamen) {
      return [];
    }
    return flurnamen
      .slice(0, flurnamen.length)
      .sort((a, b) => this.comparatorService.lagebezeichnungComparator(a, b, this.lagebezeichnungFallback, this.isErweiterungAvPlusActive));
  }
}
